/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The one-level view of a predicate tree** (`X2`, `XS1`).
 *
 * `ConditionalExpression.condition` is a `CriteriaNode` — a tree. The builder offers **one level**:
 * a list of conditions with an `all`/`any` toggle. Todd, ratifying `X2`:
 *
 * > *"if my 'and/or' list is itself a list of conditions, I think most use cases are covered, and
 * > we can move to a tree if needed. For example: my Coppa expression — if coppa **or** genre
 * > `isOneOf` "…" **or** channel = "…" then 1."*
 *
 * (`isOneOf` is `isAnyOf` since `#1287`, which widened its left side to a list. The quotation keeps
 * the word Todd used.)
 *
 * The projection lives here rather than in the component for the reason every mapping projection
 * does: it is the part with a rule in it, and a component test that clicks the control passes
 * against a projection that loses half the model.
 *
 * ## `null` is the honest answer, and it is why this is checkable
 *
 * A `not`, or a nested `all` inside an `any`, is a perfectly good predicate that a flat list
 * cannot show. `flattenPredicate` returns `null` for it and the editor **says so** rather than
 * rendering an approximation the operator could then save — `X1`'s consequence, and the rule the
 * targeting shorthand's parser followed before it was removed (`#1195`).
 */

import {
  toCriteriaNode,
  type ConditionOperator,
  type CriteriaNode,
  type MacroCondition,
} from '../../../types/decorator-expression';

/** Whether every condition must hold, or at least one. */
export type ConditionListMode = 'all' | 'any';

/** A predicate as the builder shows it: a mode and a flat list of comparisons. */
export interface ConditionList {
  mode: ConditionListMode;
  conditions: MacroCondition[];
}

/**
 * Render a predicate as a one-level list, or `null` when it is not expressible as one.
 *
 * A bare stored {@link MacroCondition} from before `XS1` is normalised on the way in, so the
 * editor opens a pre-`XS1` artifact showing its one condition rather than an empty list.
 */
export function flattenPredicate(predicate: CriteriaNode | MacroCondition | undefined | null): ConditionList | null {
  if (!predicate) return null;
  const node = toCriteriaNode(predicate);

  if (node.type === 'condition') return { mode: 'all', conditions: [node.condition] };

  if (node.type === 'all' || node.type === 'any') {
    const conditions: MacroCondition[] = [];
    for (const child of node.of) {
      // A nested group is the case the flat editor cannot show. Not "mostly" — at all.
      if (child.type !== 'condition') return null;
      conditions.push(child.condition);
    }
    return { mode: node.type, conditions };
  }

  // `not`.
  return null;
}

/**
 * Build the stored predicate from what the builder holds.
 *
 * **Always the `all`/`any` group, even at one condition.** Collapsing a single condition to a bare
 * `condition` node would silently discard a chosen `any` the moment the operator deletes back down
 * to one, and would make every reader branch on the arity for no gain — a one-element `all` and a
 * bare condition evaluate identically.
 */
export function predicateFromList(list: ConditionList): CriteriaNode {
  return {
    type: list.mode,
    of: list.conditions.map((condition) => ({ type: 'condition' as const, condition })),
  };
}

/**
 * **`NEEDS_RHS`, `rhsIsList` and `listValueMembers` were deleted here by `#1287`**, and the deletion
 * is recorded rather than silent because each was a *second implementation of a rule that has one*.
 *
 * `XS3` replaced all three with `conditionRhsShape` — the operator's own declaration (`X10`) — and
 * left them exported, used by nothing but their own tests. That is the shape `#1287` is about
 * arriving in a module rather than in a `case`: `rhsIsList` answered *which operator takes a list*
 * beside a function answering the same question, so the rename had two places to land and a spec
 * agreeing with each of them separately. `#1269`'s rule, one door along: **when a rule is written as
 * a condition in more than one place, give it a name and let both call it** — the name existed, so
 * what was owed was the deletion.
 *
 * `listValueMembers` is the same reading `readConditionOperands` now gives a stored list, in one
 * place per store.
 */

/**
 * Operators whose right-hand side is compared as **text**, so case is a question the operator has
 * to be able to answer.
 *
 * `gt`/`gte`/`lt`/`lte` coerce with `Number()` and `exists`/`notExists` have no right-hand side, so
 * offering the control on those would be offering a setting that governs nothing — `#1017`'s family
 * inside the control added to close `#1200`.
 */
export function comparesText(operator: ConditionOperator): boolean {
  return TEXT_COMPARISON.has(operator);
}

const TEXT_COMPARISON: ReadonlySet<ConditionOperator> = new Set(['eq', 'neq', 'contains', 'matches', 'isAnyOf']);

/**
 * **The row states its case mode through the `Case sensitive` checkbox, and nothing else.**
 *
 * There was a sentence here — *"Case is ignored — \"US\", \"us\" and \"Us\" all match"* — added the
 * same day the flag was, because `#1045` asks a surface to say what is in force. It was removed
 * within the hour (Todd, 2026-08-28): once the switch is **above the field with the other switch**,
 * an unticked box labelled `Case sensitive` already says it, and a sentence repeating a control is
 * `#1179`'s subject — *the prose is added per-defect and removed never*.
 *
 * The wording is recorded on `#1179` rather than deleted, because the sweep decides between the
 * `?` popover and nothing, and that is not this control's call to make.
 */
